import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Plane, Ship, FileCheck, Package, ArrowRight, CheckCircle, Globe, Clock, Shield, TrendingUp } from 'lucide-react';

const Home = () => {
  const [activeStep, setActiveStep] = useState(0);

  const services = [
    {
      icon: <Package className="w-8 h-8" />,
      title: 'Freight Forwarding',
      description: 'End-to-end coordination of your cargo movement between South Africa, Europe, and Africa with complete visibility.',
      link: '/services/freight-forwarding'
    },
    {
      icon: <FileCheck className="w-8 h-8" />,
      title: 'Customs Clearance',
      description: 'Expert customs clearance support ensuring compliance and smooth processing for imports and exports.',
      link: '/services/customs-clearance'
    },
    {
      icon: <Plane className="w-8 h-8" />,
      title: 'Air Freight',
      description: 'Fast, reliable air freight services for time-sensitive cargo between Europe and Africa.',
      link: '/services/air-freight'
    },
    {
      icon: <Ship className="w-8 h-8" />,
      title: 'Ocean Freight',
      description: 'Cost-effective sea freight solutions for LCL and FCL shipments across major trade routes.',
      link: '/services/ocean-freight'
    }
  ];

  const processSteps = [
    {
      number: '01',
      title: 'Shipment Planning',
      description: 'We assess your requirements and recommend the optimal freight solution for your cargo.'
    },
    {
      number: '02',
      title: 'Documentation & Customs',
      description: 'Complete documentation preparation and customs clearance coordination.'
    },
    {
      number: '03',
      title: 'Freight Coordination',
      description: 'Booking and managing air or ocean freight with trusted carrier partners.'
    },
    {
      number: '04',
      title: 'Clearance & Visibility',
      description: 'Real-time tracking and customs processing at destination ports.'
    },
    {
      number: '05',
      title: 'Delivery Coordination',
      description: 'Final mile coordination ensuring your cargo reaches its destination.'
    }
  ];

  const trustPoints = [
    {
      icon: <Globe className="w-10 h-10" />,
      title: 'Europe-Africa Expertise',
      description: 'Specialized knowledge of trade corridors between South Africa, Europe, and Africa.'
    },
    {
      icon: <Shield className="w-10 h-10" />,
      title: 'Customs Support',
      description: 'Expert clearing agent services ensuring compliance and smooth processing.'
    },
    {
      icon: <Clock className="w-10 h-10" />,
      title: 'Visibility & Communication',
      description: 'Clear communication and shipment tracking throughout the entire process.'
    },
    {
      icon: <TrendingUp className="w-10 h-10" />,
      title: 'Business-Focused',
      description: 'Tailored solutions for SMEs and mid-sized businesses moving commercial cargo.'
    }
  ];

  const routes = [
    {
      origin: 'Europe',
      destination: 'South Africa',
      modes: ['Air: Paris → Johannesburg', 'Ocean: Le Havre/Marseille → Durban/Cape Town/Ngqura']
    },
    {
      origin: 'South Africa',
      destination: 'Europe',
      modes: ['Air: Johannesburg → Paris', 'Ocean: Durban/Cape Town/Ngqura → Le Havre/Marseille']
    },
    {
      origin: 'Africa',
      destination: 'Europe',
      modes: ['Air & Ocean freight across major African ports']
    }
  ];

  const faqs = [
    {
      question: 'What does a freight forwarder do?',
      answer: 'A freight forwarder coordinates the movement of goods between countries, handling logistics, documentation, carrier booking, and customs coordination on behalf of importers and exporters.'
    },
    {
      question: 'What is customs clearance?',
      answer: 'Customs clearance is the process of preparing and submitting documentation to facilitate import or export of goods. A clearing agent ensures compliance with regulations, calculates duties, and coordinates with customs authorities.'
    },
    {
      question: 'Air freight vs sea freight: which should I choose?',
      answer: 'Air freight is faster (days) but more expensive, ideal for time-sensitive or high-value cargo. Ocean freight is more cost-effective (weeks) for larger shipments and non-urgent cargo. We help you choose based on your timeline and budget.'
    },
    {
      question: 'What documents are needed for import/export shipping?',
      answer: 'Typical documents include commercial invoice, packing list, bill of lading/airway bill, certificate of origin, and import/export permits. Requirements vary by cargo type and destination. We guide you through the complete documentation process.'
    }
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveStep((prev) => (prev + 1) % processSteps.length);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="overflow-hidden">
      {/* Hero Section */}
      <section className="relative min-h-[70vh] lg:min-h-[85vh] flex items-center bg-slate-50" data-testid="hero-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-24 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <div className="space-y-6">
            <div className="inline-block">
              <span className="text-xs font-mono tracking-widest uppercase text-orange-500 bg-orange-50 px-4 py-2 rounded-sm">
                Freight Forwarding & Customs Clearance
              </span>
            </div>
            <h1 
              className="text-6xl md:text-8xl font-bold tracking-tighter uppercase leading-[0.9]"
              style={{fontFamily: 'Barlow Condensed, sans-serif'}}
              data-testid="hero-title"
            >
              <span className="text-slate-900">Moving</span>{' '}
              <span className="text-orange-500">Trade</span>{' '}
              <span className="text-slate-900">Between Continents</span>
            </h1>
            <p className="text-lg md:text-xl leading-relaxed text-slate-600 max-w-xl">
              NETEXEC GLOBAL helps South African businesses move import and export cargo through freight forwarding, customs clearance, and end-to-end logistics coordination between South Africa, Europe, and Africa.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 pt-4">
              <Link
                to="/contact"
                data-testid="hero-quote-btn"
                className="bg-slate-900 text-white px-8 py-4 rounded-sm text-base font-medium hover:bg-orange-500 transition-colors duration-300 inline-flex items-center justify-center gap-2"
              >
                Request a Quote
                <ArrowRight className="w-5 h-5" />
              </Link>
              <Link
                to="/services"
                data-testid="hero-services-btn"
                className="bg-transparent border-2 border-slate-200 text-slate-900 px-8 py-4 rounded-sm text-base font-medium hover:border-slate-900 transition-colors inline-flex items-center justify-center"
              >
                Our Services
              </Link>
            </div>
          </div>

          {/* Right Image */}
          <div className="relative h-[400px] lg:h-[600px] rounded-sm overflow-hidden shadow-2xl">
            <img
              src="https://images.unsplash.com/photo-1758900348926-cd1ccbc15fef?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85"
              alt="Aerial view of cargo ship sailing on ocean"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-slate-900/20 to-transparent"></div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-24 md:py-32 bg-white" data-testid="services-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <span className="text-xs font-mono tracking-widest uppercase text-orange-500">What We Do</span>
            <h2 
              className="text-4xl md:text-6xl font-bold tracking-tight uppercase mt-4"
              style={{fontFamily: 'Barlow Condensed, sans-serif'}}
            >
              Our Services
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {services.map((service, index) => (
              <Link
                key={index}
                to={service.link}
                data-testid={`service-card-${index}`}
                className="bg-white border border-slate-200 p-8 rounded-sm hover:shadow-xl hover:shadow-slate-200/50 transition-all duration-300 hover:-translate-y-1 group"
              >
                <div className="text-orange-500 mb-4 group-hover:scale-110 transition-transform duration-300">
                  {service.icon}
                </div>
                <h3 className="text-2xl font-semibold mb-3 text-slate-900">{service.title}</h3>
                <p className="text-base leading-relaxed text-slate-600 mb-4">{service.description}</p>
                <div className="flex items-center text-orange-500 font-medium text-sm">
                  Learn More <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-2 transition-transform" />
                </div>
              </Link>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link
              to="/services"
              className="bg-transparent border-2 border-slate-200 text-slate-900 px-8 py-4 rounded-sm text-base font-medium hover:border-slate-900 transition-colors inline-flex items-center gap-2"
            >
              View All Services
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-24 md:py-32 bg-slate-50" data-testid="process-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <span className="text-xs font-mono tracking-widest uppercase text-orange-500">Our Process</span>
            <h2 
              className="text-4xl md:text-6xl font-bold tracking-tight uppercase mt-4"
              style={{fontFamily: 'Barlow Condensed, sans-serif'}}
            >
              How We Help
            </h2>
            <p className="text-lg md:text-xl leading-relaxed text-slate-600 mt-4 max-w-3xl mx-auto">
              From initial planning to final delivery, we coordinate every step of your cargo's journey.
            </p>
          </div>

          {/* Desktop Process - Horizontal */}
          <div className="hidden md:block relative">
            <div className="flex justify-between items-start gap-4">
              {processSteps.map((step, index) => (
                <div
                  key={index}
                  className="flex-1 text-center relative"
                  data-testid={`process-step-${index}`}
                >
                  <div
                    className={`inline-flex items-center justify-center w-16 h-16 rounded-full border-2 mb-4 transition-all duration-500 ${
                      activeStep === index
                        ? 'bg-orange-500 border-orange-500 text-white scale-110'
                        : 'bg-white border-slate-300 text-slate-600'
                    }`}
                  >
                    <span className="text-lg font-bold" style={{fontFamily: 'JetBrains Mono, monospace'}}>
                      {step.number}
                    </span>
                  </div>
                  {index < processSteps.length - 1 && (
                    <div className="absolute top-8 left-[60%] w-[80%] h-0.5 bg-slate-200">
                      <div
                        className="h-full bg-orange-500 transition-all duration-500"
                        style={{ width: activeStep > index ? '100%' : '0%' }}
                      ></div>
                    </div>
                  )}
                  <h3 className="font-semibold text-slate-900 mb-2">{step.title}</h3>
                  <p className="text-sm text-slate-600">{step.description}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Mobile Process - Vertical */}
          <div className="md:hidden space-y-6">
            {processSteps.map((step, index) => (
              <div key={index} className="flex gap-4">
                <div className="flex flex-col items-center">
                  <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-orange-500 text-white">
                    <span className="text-sm font-bold" style={{fontFamily: 'JetBrains Mono, monospace'}}>
                      {step.number}
                    </span>
                  </div>
                  {index < processSteps.length - 1 && (
                    <div className="w-0.5 flex-1 bg-slate-200 mt-2"></div>
                  )}
                </div>
                <div className="flex-1 pb-6">
                  <h3 className="font-semibold text-slate-900 mb-2">{step.title}</h3>
                  <p className="text-sm text-slate-600">{step.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Trade Corridor Section */}
      <section className="py-24 md:py-32 bg-white" data-testid="trade-routes-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="text-xs font-mono tracking-widest uppercase text-orange-500">Trade Corridors</span>
              <h2 
                className="text-4xl md:text-6xl font-bold tracking-tight uppercase mt-4 mb-6"
                style={{fontFamily: 'Barlow Condensed, sans-serif'}}
              >
                Europe-Africa Expertise
              </h2>
              <p className="text-lg leading-relaxed text-slate-600 mb-8">
                NETEXEC GLOBAL specializes in freight movement across the Europe–Africa corridor, with established routes connecting South Africa to major European hubs.
              </p>
              <Link
                to="/trade-routes"
                className="bg-slate-900 text-white px-8 py-4 rounded-sm text-base font-medium hover:bg-orange-500 transition-colors duration-300 inline-flex items-center gap-2"
              >
                Explore Trade Routes
                <ArrowRight className="w-5 h-5" />
              </Link>
            </div>

            <div className="space-y-4">
              {routes.map((route, index) => (
                <div
                  key={index}
                  className="bg-slate-50 border border-slate-200 p-6 rounded-sm"
                  data-testid={`route-card-${index}`}
                >
                  <div className="flex items-center gap-3 mb-3">
                    <span className="font-bold text-slate-900">{route.origin}</span>
                    <ArrowRight className="w-5 h-5 text-orange-500" />
                    <span className="font-bold text-slate-900">{route.destination}</span>
                  </div>
                  <ul className="space-y-2">
                    {route.modes.map((mode, mIndex) => (
                      <li key={mIndex} className="flex items-start gap-2 text-sm text-slate-600">
                        <CheckCircle className="w-4 h-4 text-orange-500 mt-0.5 flex-shrink-0" />
                        {mode}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Trust Section */}
      <section className="py-24 md:py-32 bg-slate-50" data-testid="trust-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <span className="text-xs font-mono tracking-widest uppercase text-orange-500">Why Choose Us</span>
            <h2 
              className="text-4xl md:text-6xl font-bold tracking-tight uppercase mt-4"
              style={{fontFamily: 'Barlow Condensed, sans-serif'}}
            >
              Why Businesses Choose NETEXEC
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {trustPoints.map((point, index) => (
              <div
                key={index}
                className="text-center"
                data-testid={`trust-point-${index}`}
              >
                <div className="inline-flex items-center justify-center w-20 h-20 bg-orange-50 text-orange-500 rounded-sm mb-4">
                  {point.icon}
                </div>
                <h3 className="font-semibold text-slate-900 mb-2 text-lg">{point.title}</h3>
                <p className="text-sm text-slate-600">{point.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-24 md:py-32 bg-white" data-testid="faq-section">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <span className="text-xs font-mono tracking-widest uppercase text-orange-500">Knowledge Hub</span>
            <h2 
              className="text-4xl md:text-6xl font-bold tracking-tight uppercase mt-4"
              style={{fontFamily: 'Barlow Condensed, sans-serif'}}
            >
              Frequently Asked Questions
            </h2>
          </div>

          <div className="space-y-6">
            {faqs.map((faq, index) => (
              <div
                key={index}
                className="bg-slate-50 border border-slate-200 p-6 rounded-sm"
                data-testid={`faq-item-${index}`}
              >
                <h3 className="font-semibold text-slate-900 mb-3 text-lg">{faq.question}</h3>
                <p className="text-base text-slate-600 leading-relaxed">{faq.answer}</p>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link
              to="/faq"
              className="bg-transparent border-2 border-slate-200 text-slate-900 px-8 py-4 rounded-sm text-base font-medium hover:border-slate-900 transition-colors inline-flex items-center gap-2"
            >
              View All FAQs
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 md:py-32 bg-slate-900 text-white" data-testid="cta-section">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <h2 
            className="text-4xl md:text-6xl font-bold tracking-tight uppercase mb-6"
            style={{fontFamily: 'Barlow Condensed, sans-serif'}}
          >
            Ready to Move Your Cargo?
          </h2>
          <p className="text-lg md:text-xl leading-relaxed text-slate-300 mb-8 max-w-2xl mx-auto">
            Get a customized quote for your import or export shipment. Our team will respond within 24 hours.
          </p>
          <Link
            to="/contact"
            data-testid="cta-quote-btn"
            className="bg-orange-500 text-white px-10 py-5 rounded-sm text-lg font-medium hover:bg-orange-600 transition-colors duration-300 inline-flex items-center gap-2"
          >
            Request a Quote
            <ArrowRight className="w-6 h-6" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Home;